/**
 * L'onglet « Évolution » de la fiche d'un élève — chantier « manuel vivant »,
 * Φ-MV-5 (MV-TUT-08, MV-UX-03).
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves/:eleveId/evolution`. Cinq
 * semaines, du lundi au dimanche : le PALIER de chaque notion à la fin de
 * chaque semaine, et l'activité de chaque semaine en COMPTES. Le serveur
 * recalcule les paliers passés depuis le journal ; l'écran les pose.
 *
 * ─── Ce qui n'est pas porté de la maquette, et ne le sera pas ───────────────
 *
 * La courbe de la maquette, son échelle de zéro à cent, ses vignettes et ses
 * colonnes chiffrées : elles contredisent l'invariant du produit
 * (`DECISIONS-ARCHITECTE.md`, point 17 — même pour le tuteur). À la place, par
 * notion, cinq cases dont les crans et le mot disent le palier. Aucune barre
 * non plus : une barre de réussites rapportée aux essais est une proportion
 * dessinée ; les deux comptes se lisent côte à côte.
 *
 * ─── Les semaines qui comptent (revue UX `UX-7-32`) ─────────────────────────
 *
 * Le serveur rend cinq semaines ; pour un élève arrivé cette semaine, quatre
 * d'entre elles ne disaient que « Nouveau » et zéro. On ne montre que les
 * semaines DEPUIS LE DÉBUT DE L'OBJECTIF — ou depuis le premier travail, s'il
 * est antérieur : aucun travail n'est jamais retiré de l'écran. Et une notion
 * sans aucun essai à la fin d'une semaine laisse sa case vide.
 *
 * ─── La charge (F-11) ───────────────────────────────────────────────────────
 *
 * Ouverte : « Paliers par notion », ce pour quoi on vient. Repliée, avec ses
 * deux sommes en résumé : « Travaux par semaine ».
 */
import { denombrer, type EvolutionEleveResponse } from '@silex/shared';
import { useMemo } from 'react';

import { api } from '../../api/client';
import { CransNotion } from '../../components/CransNotion';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { SectionRabattable } from '../../components/SectionRabattable';
import { jourCivil, nomDeNotion } from '../../lib/manuel-tuteur-format';
import {
  nomDeSemaine,
  premiereSemaineUtile,
  resumeSemaines,
} from '../../lib/manuel-tuteur-lectures';
import { useAsync } from '../../lib/useAsync';

export function EvolutionEleve({
  eleveId,
  rangChapitre,
  depuis,
}: {
  readonly eleveId: string;
  readonly rangChapitre: number;
  /** Le début de l'objectif (date civile) ; `null` : aucun objectif, toutes les semaines. */
  readonly depuis: string | null;
}) {
  const resource = useAsync(useMemo(() => () => api.manuelEvolution(eleveId), [eleveId]));

  if (resource.status === 'loading') return <Chargement quoi="son évolution" />;
  if (resource.status === 'error') return <ErreurRessource message={resource.message} />;
  return <Evolution evolution={resource.data} rangChapitre={rangChapitre} depuis={depuis} />;
}

/** L'onglet, une fois lu : sans appel, il s'éprouve par son rendu. */
export function Evolution({
  evolution: e,
  rangChapitre,
  depuis = null,
}: {
  readonly evolution: EvolutionEleveResponse;
  readonly rangChapitre: number;
  readonly depuis?: string | null;
}) {
  /* Les paliers d'une notion sont rendus dans l'ordre des semaines : on retire
     les mêmes premières colonnes des deux tableaux. */
  const debut = premiereSemaineUtile(e.semaines, depuis);
  const semaines = e.semaines.slice(debut);
  return (
    <div className="stack" data-testid="manuel-evolution">
      {e.vide ? (
        <p className="note-line" data-testid="manuel-evolution-vide">
          <strong>Aucun travail enregistré.</strong> Les paliers se lisent dès le premier exercice.
        </p>
      ) : null}

      <div data-testid="manuel-evolution-paliers">
        <SectionRabattable
          titre="Paliers par notion"
          resume={`${denombrer(e.notions.length, 'notion')} · ${denombrer(semaines.length, 'semaine')}`}
          sous={`Le palier à la fin de chaque semaine · au ${jourCivil(e.aujourdhui)}`}
          cleMemoire="manuel.evolution.paliers"
        >
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Notion</th>
                  {semaines.map((s) => (
                    <th key={s.du} scope="col">
                      {s.enCours ? null : <span className="cell-sub">Semaine du</span>}
                      {nomDeSemaine(s)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {e.notions.map((n) => (
                  <tr key={n.notion.code} data-testid="manuel-evolution-notion">
                    <th scope="row">{nomDeNotion(n.notion, rangChapitre)}</th>
                    {n.paliers.slice(debut).map((p) => (
                      <td key={p.au}>
                        {/* « Nouveau » : aucun essai à cette date — la case reste vide. */}
                        {p.palier === 'nouveau' ? (
                          <span className="cell-sub" aria-label="Pas encore travaillée">
                            —
                          </span>
                        ) : (
                          <CransNotion palier={p.palier} libelle={p.libellePalier} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionRabattable>
      </div>

      <div data-testid="manuel-evolution-semaines">
        <SectionRabattable
          titre="Travaux par semaine"
          resume={resumeSemaines(semaines)}
          sous="Essais, défis et devoirs corrigés, du lundi au dimanche."
          ouverteParDefaut={false}
          cleMemoire="manuel.evolution.semaines"
        >
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Semaine du</th>
                  <th scope="col">Travaux</th>
                  <th scope="col">Réussis</th>
                  <th scope="col">Dont essais</th>
                  <th scope="col">Dont devoirs</th>
                  <th scope="col">Minutes</th>
                </tr>
              </thead>
              <tbody>
                {semaines.map((s) => (
                  <tr key={s.du} data-testid="manuel-evolution-semaine">
                    <th scope="row">
                      {jourCivil(s.du)}
                      {s.enCours ? <span className="cell-sub">en cours</span> : null}
                    </th>
                    <td>
                      <span className="data">{s.travaux}</span>
                    </td>
                    <td>
                      <span className="data">{s.reussis}</span>
                    </td>
                    <td>
                      <span className="data">{s.essais}</span>
                    </td>
                    <td>
                      <span className="data">{s.devoirs}</span>
                    </td>
                    <td>
                      <span className="data">{s.minutes}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionRabattable>
      </div>
    </div>
  );
}
